/* eslint-disable no-param-reassign */
import { useEffect, useRef } from 'react';

import PatientModel from 'jsx/models/patient/patient';
import CONFIG from 'jsx/constants/config';
import errorReporter from 'jsx/utils/errorReporter';

declare global {
  interface Window {
    duet?: {
      open(): void;
      setMetadata: (metadata: {
        email?: string;
        name?: string;
        path?: string;
      }) => void;
      setUserId: (userId: string) => void;
      setUserIdAuth: (signature: string, epoch: number) => void;
    };
  }
}

interface SignatureToken {
  user_id: string;
  signature: string;
  epoch: number;
}

const ENDPOINT = '/api/v1/decagon/auth/signature-token';
const FRESHNESS_MARGIN_S = 5 * 60; // reused until this close to the 24h expiry

// Module-scope, per-patient token cache (survives SPA nav, cleared on full reload);
// inFlightRequests dedupes concurrent mounts. No timer — reused until near expiry.
const resolvedTokens = new Map<string, SignatureToken>();
const inFlightRequests = new Map<string, Promise<SignatureToken | null>>();

// Loader <script> outcome, shared with the iframe poller: only a loader that loaded
// but never injected is a real failure; blocked/unreachable means no widget at all.
let loaderScriptStatus: 'pending' | 'loaded' | 'error' = 'pending';
let hasReportedMissingIframe = false;

/** Resets all module-level Decagon hook state. Test-only. */
export const resetDecagonHookStateForTests = (): void => {
  resolvedTokens.clear();
  inFlightRequests.clear();
  loaderScriptStatus = 'pending';
  hasReportedMissingIframe = false;
};

const isFresh = (token: SignatureToken): boolean => {
  const nowSeconds = Math.floor(Date.now() / 1000);
  return token.epoch > nowSeconds + FRESHNESS_MARGIN_S;
};

// A cached token is reusable only if it belongs to this patient and is not near expiry.
const getReusableToken = (cacheKey: string): SignatureToken | null => {
  const token = resolvedTokens.get(cacheKey);
  return token && token.user_id === cacheKey && isFresh(token) ? token : null;
};

const reportSignatureTokenError = (err: unknown): null => {
  const status =
    err !== null && typeof err === 'object' && 'status' in err
      ? (err as { status: number }).status
      : undefined;
  // Report real server errors (non-401) and a malformed 200 body; status-less
  // failures (abort/blocked/offline) stay silent, like a 401.
  const isServerError = typeof status === 'number' && status !== 401;
  if (isServerError || err instanceof SyntaxError) {
    errorReporter.notifyException(
      new Error('Decagon signature token fetch failed'),
      { context: 'useDecagonChatBot', metadata: { status } },
    );
  }
  return null;
};

// Cache only a self-consistent token for this patient; never cache a null result.
const cacheResolvedToken = (
  cacheKey: string,
  token: SignatureToken,
): SignatureToken | null => {
  if (token.user_id === cacheKey) resolvedTokens.set(cacheKey, token);
  return token;
};

const requestSignatureToken = (
  cacheKey: string,
): Promise<SignatureToken | null> =>
  // ask for JSON so a 401 stays a 401 — else Laravel redirects and stores this url as url.intended for the next login
  fetch(ENDPOINT, { headers: { Accept: 'application/json' } })
    .then((res) =>
      res.ok ? (res.json() as Promise<SignatureToken>) : Promise.reject(res),
    )
    .then((token) => cacheResolvedToken(cacheKey, token))
    .catch(reportSignatureTokenError);

// Returns a fresh cached token, an in-flight request, or a new fetch — one per patient.
const getSignatureToken = (
  cacheKey: string,
): Promise<SignatureToken | null> => {
  const reusable = getReusableToken(cacheKey);
  if (reusable) return Promise.resolve(reusable);

  const inFlight = inFlightRequests.get(cacheKey);
  if (inFlight) return inFlight;

  const request = requestSignatureToken(cacheKey).finally(() => {
    inFlightRequests.delete(cacheKey);
  });
  inFlightRequests.set(cacheKey, request);
  return request;
};

const onDecagonIframeLoad = (
  decagonIframe: HTMLElement,
  patient: PatientModel,
  tokenPromise: { current: Promise<SignatureToken | null> | null },
) => {
  window.duet?.setMetadata({
    email: patient.email,
    name: patient.fullName,
    path: window.location.pathname,
  });

  tokenPromise.current?.then((token) => {
    if (token) {
      window.duet?.setUserId(token.user_id);
      window.duet?.setUserIdAuth(token.signature, token.epoch);
    }
  });

  decagonIframe.style.bottom = '60px';
  decagonIframe.style.maxHeight = 'calc(100dvh - 60px)';

  decagonIframe.style.zIndex = '999994';
};

const IFRAME_POLL_INTERVAL_MS = 50;
const IFRAME_MAX_ATTEMPTS = 600; // ~30s, then give up (bounds a runaway poll)

const reportMissingIframeOnce = (): void => {
  // Report once per page only when the loader loaded but never injected (real failure);
  // a blocked/unreachable loader means no widget at all and stays silent.
  if (hasReportedMissingIframe || loaderScriptStatus !== 'loaded') return;
  hasReportedMissingIframe = true;

  errorReporter.notifyException(
    new Error('Decagon loader ran but did not inject its iframe'),
    { context: 'useDecagonChatBot' },
  );
};

// Polls for the iframe and attaches one load listener; returns a disposer that
// cancels the poll and removes the listener on unmount.
const decagonIframeLoaded = (
  brand: string,
  callback: (decagonIframe: HTMLElement) => void,
): (() => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let iframe: HTMLElement | undefined;
  let onLoad: (() => void) | undefined;
  let attempts = 0;

  const run = () => {
    if (typeof document === 'undefined') return;

    const decagonIframe = document.querySelector(
      `[src="https://decagon.ai/demo/${brand}"]`,
    );

    if (decagonIframe) {
      iframe = decagonIframe as HTMLElement;
      onLoad = () => callback(iframe as HTMLElement);
      iframe.addEventListener('load', onLoad);
      return;
    }

    attempts += 1;
    if (attempts < IFRAME_MAX_ATTEMPTS) {
      timeoutId = setTimeout(run, IFRAME_POLL_INTERVAL_MS);
    } else {
      reportMissingIframeOnce();
    }
  };

  timeoutId = setTimeout(run, IFRAME_POLL_INTERVAL_MS);

  return () => {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
    if (iframe && onLoad) iframe.removeEventListener('load', onLoad);
  };
};

export const useDecagonChatBot = (patient?: PatientModel) => {
  const brand = window.CUROLOGY_ENV.APP_BRAND ?? patient?.brand;
  const tokenPromise = useRef<Promise<SignatureToken | null> | null>(null);

  useEffect(() => {
    if (!patient || CONFIG.isTestEnv) return;

    tokenPromise.current = getSignatureToken(String(patient.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id]);

  useEffect(() => {
    // We only want this to run once.
    if (
      !!document.getElementById('decagon-script') ||
      !brand ||
      !patient ||
      CONFIG.isTestEnv
    ) {
      return;
    }

    const script = document.createElement('script');
    script.setAttribute('id', 'decagon-script');
    script.src = `https://decagon.ai/loaders/${brand}.js`;
    script.async = true;
    // Track loader outcome so the poller can tell "blocked" from "loaded but no iframe".
    script.onload = () => {
      loaderScriptStatus = 'loaded';
    };
    script.onerror = () => {
      loaderScriptStatus = 'error';
    };

    document.body.appendChild(script);
  }, [brand, patient]);

  useEffect(() => {
    if (!brand || !patient || CONFIG.isTestEnv) return undefined;

    return decagonIframeLoaded(brand, (decagonIframe) =>
      onDecagonIframeLoad(decagonIframe, patient, tokenPromise),
    );
  }, [brand, patient]);
};
